<template>
    <div class="flex flex-col bg-white rounded shadow md:flex-row">
        <calendar-events-get
            :starts-after="now.startOf('month').format('X')"
            :ends-before="now.endOf('month').format('X')"
        >
            <div
                slot-scope="{
                    has_loaded,
                    is_loading,
                    error,
                    data: calendar_events
                }"
                class="p-6 md:min-w-1/3 md:max-w-xs"
            >
                <base-calendar v-model="now">
                    <template
                        slot="header"
                        slot-scope="{ month, nextMonth, prevMonth }"
                    >
                        <div class="flex items-center mb-3">
                            <button
                                type="button"
                                @click.prevent="prevMonth"
                                class="w-6 h-6 mr-4"
                            >
                                <base-svg
                                    name="icon-cheveron-left"
                                    class="w-full h-full fill-primary-grey-darker"
                                />
                            </button>

                            <div class="flex-grow text-center">
                                {{ month | moment('MMM YYYY') }}
                            </div>

                            <base-svg
                                :class="{'invisible': !is_loading}"
                                name="icon-loading"
                                class="w-4 h-4 fill-primary-grey"
                            />

                            <button
                                type="button"
                                @click.prevent="nextMonth"
                                class="w-6 h-6"
                            >
                                <base-svg
                                    name="icon-cheveron-right"
                                    class="w-full h-full fill-primary-grey-darker"
                                />
                            </button>
                        </div>
                    </template>

                    <template
                        slot="weekday"
                        slot-scope="{ weekday }"
                    >
                        <div class="w-full pb-1 text-xs font-bold text-center text-grey">
                            {{ weekday }}
                        </div>
                    </template>

                    <template
                        slot="date"
                        slot-scope="{ date }"
                    >
                        <div
                            v-if="date.is_current_month"
                            :class="[
                                { 'border ': date.is_today },
                                selected_date.isSame(date.date, 'day')
                                    ? 'text-white bg-blue hover:bg-blue-dark'
                                    : 'hover:bg-grey-lighter'
                            ]"
                            class="w-full h-full py-2 text-xs font-semibold text-center rounded cursor-pointer"
                            @click.prevent="selected_date = date.date.clone()"
                        >
                            <div class="mt-1">
                                {{ date.date | moment('D') }}
                            </div>

                            <div class="h-1 mx-auto text-0 -ml-2px">
                                <div
                                    v-if="eventsByDate(date.date, calendar_events).length"
                                    :class="selected_date.isSame(date.date, 'day') ? 'bg-white' : 'bg-blue'"
                                    class="inline-block w-1 h-1 rounded-full mr-2px"
                                />
                            </div>
                        </div>

                        <div v-else />
                    </template>
                </base-calendar>

                <error-modal
                    v-if="error"
                    :error="error"
                />
            </div>
        </calendar-events-get>

        <calendar-events-get
            :starts-after="selected_date.format('X')"
            :ends-before="selected_date.clone().add(14, 'days').format('X')"
        >
            <div
                slot-scope="{
                    is_loading,
                    has_loaded,
                    error,
                    data: calendar_events,
                    get
                }"
                class="relative flex-grow border-t rounded-b bg-grey-bg min-h-2xs md:border-t-0 md:border-l md:rounded-bl-none md:rounded-r"
            >
                <div class="absolute inset-0 overflow-y-auto scrollbar-hidden p-6">
                    <template v-if="is_loading || error">
                        <base-loading
                            :error="error"
                            :retry="get"
                            class="py-6"
                        />

                        <error-modal
                            v-if="error"
                            :error="error"
                        />
                    </template>

                    <template v-else>
                        <div class="mb-3">
                            Schedule
                        </div>

                        <div
                            v-for="(date, index) in transform(calendar_events)"
                            :key="index"
                            class="mb-3 pt-3 border-b "
                        >
                            <div class="text-label text-2xs mb-2">
                                {{ date.date | moment('ddd D MMM, YYYY') }}
                            </div>

                            <div
                                v-if="!date.events.length"
                                class="flex mb-3"
                            >
                                <div class="italic text-sm text-grey">Nothing planned.</div>
                            </div>
                            <div
                                v-else
                                v-for="event in date.events"
                                :key="event.uuid"
                                class="flex mb-3"
                            >
                                <component
                                    :is="event.path ? 'router-link' : 'div'"
                                    :to="event.path ? { path: event.path } : null"
                                    class="flex-grow no-underline"
                                >
                                    <div
                                        :class="{ 'text-blue': event.path }"
                                        class="text-sm"
                                    >
                                        {{ event.title }}
                                    </div>
                                    <div class="text-xs text-grey-darker">{{ event.description }}</div>
                                </component>
                            </div>
                        </div>
                    </template>
                </div>
            </div>
        </calendar-events-get>
    </div>
</template>

<script>
    import _ from 'lodash'
    import moment from 'moment'
    import BaseCalendar from '../../../../../components/base-calendar'
    import CalendarEventsGet from '../../../../../components/calendar-events-get'

    export default {
        components: {
            BaseCalendar,
            CalendarEventsGet
        },
        data () {
            return {
                now: moment(),
                selected_date: moment()
            }
        },
        methods: {
            transform (events) {
                events = _.chain(events)
                    .map(e => {
                        return _.extend({}, e, {
                            starts_at: moment(e.starts_at),
                            ends_at: moment(e.ends_at)
                        })
                    })
                    .value()

                return _.chain(_.range(0, 14))
                    .map(i => this.selected_date.clone().add(i, 'days'))
                    .map(date => {
                        return {
                            date,
                            events: _.filter(events, (evt) => {
                                return evt.starts_at.isSame(date, 'day')
                            })
                        }
                    })
                    .value()
            },
            eventsByDate (date, events) {
                return _.filter(events, (evt) => {
                    return moment(evt.starts_at).isSame(date, 'day')
                })
            }
        }
    }
</script>
